<template>
  <div class="map-elevation-view p-4">

    <!-- Live preview -->
    <div ref="previewEl" class="elevation-preview mb-4"></div>

    <div class="d-flex justify-content-between align-items-center mb-3">
      <p class="section-label mb-0">Exaggeration level</p>
      <span class="value-label">{{ displayValue }}</span>
    </div>

    <input
      type="range"
      class="elevation-slider w-100"
      min="0.1"
      max="20"
      step="0.1"
      :value="exaggeration"
      @input="onInput(+($event.target as HTMLInputElement).value)"
    />

    <div class="d-flex justify-content-between mt-1">
      <small class="text-muted">0.1</small>
      <small class="text-muted">20.0</small>
    </div>

    <button class="reset-btn mt-3" @click="resetToDefault">Reset to default</button>

  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import mapboxgl from 'mapbox-gl';
import { getMapSettings, saveMapSettings } from '../../../utils/mapSettings';
import { ThreeDimensionalMapManager } from '../../../utils/models/MapStyles';

export default defineComponent({
  name: 'MapElevationView',

  props: {
    mapInstanceName: { type: String, default: 'map' },
  },

  data() {
    return {
      exaggeration: getMapSettings().elevationExaggeration,
      previewMap: null as mapboxgl.Map | null,
    };
  },

  computed: {
    displayValue(): string {
      return this.exaggeration.toFixed(1);
    },
  },

  mounted() {
    try {
      const map = (window as Record<string, any>)[this.mapInstanceName] as mapboxgl.Map;
      this.initPreview(map);
    } catch {}
  },

  beforeUnmount() {
    try {
      this.previewMap?.remove();
    } catch {}
    this.previewMap = null;
  },

  methods: {
    initPreview(mainMap: mapboxgl.Map) {
      const el = this.$refs.previewEl as HTMLElement | undefined;
      if (!mainMap || !el) return;

      this.previewMap = new mapboxgl.Map({
        container: el,
        style: mainMap.getStyle() as any,
        center: [-5.0035, 56.7969],
        zoom: 12,
        bearing: 30,
        pitch: 70,
        interactive: false,
        attributionControl: false,
      });

      this.previewMap.once('load', () => {
        if (!this.previewMap) return;
        if (!this.previewMap.getSource('mapbox-dem')) {
          this.previewMap.addSource('mapbox-dem', {
            type: 'raster-dem',
            url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
            tileSize: 512,
            maxzoom: 14,
          });
        }
        this.previewMap.setTerrain({ source: 'mapbox-dem', exaggeration: this.exaggeration });
        this.previewMap.resize();
      });
    },

    resetToDefault() {
      this.onInput(1.0);
    },

    onInput(value: number) {
      this.exaggeration = value;
      const settings = getMapSettings();
      settings.elevationExaggeration = value;
      saveMapSettings(settings);
      try {
        const map = (window as Record<string, any>)[this.mapInstanceName];
        if (!map) return;
        if (!map.getTerrain()) {
          if (this.mapInstanceName === 'map') {
            ThreeDimensionalMapManager.enable3DMode();
          } else {
            if (!map.getSource('mapbox-dem')) {
              map.addSource('mapbox-dem', {
                type: 'raster-dem',
                url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
                tileSize: 512,
                maxzoom: 14,
              });
            }
            map.setPitch(map.getPitch() || 50);
          }
        }
        map.setTerrain({ source: 'mapbox-dem', exaggeration: value });
      } catch {}

      // Update preview in real-time
      try {
        if (this.previewMap?.getSource('mapbox-dem')) {
          this.previewMap.setTerrain({ source: 'mapbox-dem', exaggeration: value });
        }
      } catch {}
    },
  },
});
</script>

<style scoped>
.elevation-preview {
  width: 100%;
  height: 180px;
  border-radius: 14px;
  overflow: hidden;
  background: #1a1a1a;
}

.section-label {
  font-size: 0.75rem;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.value-label {
  color: #fddf00;
  font-weight: 700;
  font-size: 0.95rem;
}

.elevation-slider {
  accent-color: #fddf00;
  cursor: pointer;
}

.reset-btn {
  width: 100%;
  padding: 12px;
  background: #2d2d2d;
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
}

.reset-btn:hover {
  background: #3a3a3a;
}
</style>
